import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const languages = [
  { code: "fr", label: "Français", flag: "🇨🇩" },
  { code: "en", label: "English", flag: "🇺🇸" },
];

export default function LanguageSelector({ mobile = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);

  const switchLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("djilu_language", code);
    setOpen(false);
  };

  if (mobile) {
    return (
      <div className="flex gap-2" data-testid="lang-selector-mobile">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => switchLang(lang.code)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border",
              i18n.language === lang.code
                ? "bg-[#FFC928]/10 text-[#FFC928] border-[#FFC928]/20"
                : "bg-white/[0.05] text-[#94A3B8] border-white/[0.08] hover:text-white"
            )}
          >
            <span>{lang.flag}</span>
            <span>{lang.label}</span>
            {i18n.language === lang.code && <Check size={14} />}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="lang-selector">
      <button
        onClick={() => setOpen(!open)}
        aria-label={t("common.selectLanguage")}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] text-sm font-medium hover:bg-white/[0.08] hover:text-white transition-all"
        data-testid="lang-toggle-btn"
      >
        <Globe size={15} />
        <span className="uppercase">{current.code}</span>
        <ChevronDown size={13} className={cn("transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("common.selectLanguage")}
          className="absolute right-0 top-full mt-2 w-44 bg-[#0F172A] border border-[#FFC928]/20 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={i18n.language === lang.code}
              onClick={() => switchLang(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors",
                i18n.language === lang.code
                  ? "text-[#FFC928] bg-[#FFC928]/10"
                  : "text-[#94A3B8] hover:bg-white/[0.05] hover:text-white"
              )}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="font-medium">{lang.label}</span>
              {i18n.language === lang.code && <Check size={14} className="ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
